import Button from "@mui/material/Button";
import Icon from "@mui/material/Icon";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import { styled } from "@mui/material/styles";
import { useConfirmDialog, ConfirmResponse } from "_shared/_providers/dialogs/confirm/confirm-dialog";
import EntityOfficerInvitationAPIService from "legal-entities/invitations/officer-invitations/_api/entity-officer-invitation-api-service";
import EntityOfficerInvitation from "legal-entities/invitations/officer-invitations/entity-officer-invitation";
import { enqueueSnackbar } from "notistack";
import Session from "users/__session/session";

const InvitationsTable = styled("div")(({ theme }) => ({
  padding: theme.spacing(1),
  position: "relative",
  width: "100%"
}));

const InvitationsTableTitle = styled(Typography)(({ theme }) => ({
  marginBottom: theme.spacing(1.5)
}));

type EntityOfficerInvitationsTableProps = {
  invitations: EntityOfficerInvitation[],
  onInvitationRemoved: (invitation: EntityOfficerInvitation) => void,
  session: Readonly<Session>
}

export default function EntityOfficerInvitationsTable(props: Readonly<EntityOfficerInvitationsTableProps>) {
  const { invitations, onInvitationRemoved, session } = props;

  const confirm = useConfirmDialog();

  async function handleCancelInvite(invitation: EntityOfficerInvitation) {
    const response = await confirm({
      title: "Cancel Invitation?",
      message: "Are you sure you want to cancel this entity officer invitation?",
      okButtonText: "Yes",
      cancelButtonText: "No"
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      const service = new EntityOfficerInvitationAPIService(session);
      await service.cancelEntityOfficerInvitation(invitation);
      onInvitationRemoved(invitation);
      enqueueSnackbar("Canceled entity officer invitation", { variant: "success" });
    } catch (ex) {
      console.error(ex);
      enqueueSnackbar("Failed to cancel entity officer invitation", { variant: "error" });
    }
  }

  return (
    <InvitationsTable>
      <InvitationsTableTitle variant="h6">Entity Officer Invitations</InvitationsTableTitle>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell align="left">Email Address</TableCell>
            <TableCell align="left">Entity</TableCell>
            <TableCell align="left">Message</TableCell>
            <TableCell align="right">Action</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {(invitations.length < 1) && (
            <TableRow>
              <TableCell colSpan={4} align="center">
                <Typography>No Pending Invitations</Typography>
              </TableCell>
            </TableRow>
          )}
          {invitations.map((invitation: EntityOfficerInvitation) => (
            <TableRow className="active-officer-invitation" key={invitation.id?.value} data-invitation-id={invitation.id?.value}>
              <TableCell align="left">
                <Typography>
                  {invitation.registrationInfo?.email?.value ?? ''}
                </Typography>
              </TableCell>
              <TableCell align="left">
                <Typography>{invitation.entityName.value}</Typography>
              </TableCell>
              <TableCell align="left">
                <Typography>{invitation.message?.value ?? <Icon>remove</Icon>}</Typography>
              </TableCell>
              <TableCell align="right">
                <Button
                  onClick={() => handleCancelInvite(invitation)}
                  color="primary">
                  Cancel
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </InvitationsTable>
  );
}
